import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAttorneySurfaceConfig } from "../../../../features/bonds/shared/attorneyInformationConfig";
import { useAttorneyInformationCore } from "../../../../features/bonds/shared/hooks/useAttorneyInformationCore";

/**
 * Bond retrieval attorney step state/handlers (Phase 14 / 9.10B).
 * Delegates shared CRUD to useAttorneyInformationCore with surface="retrieval".
 */
export function useRetrievalAttorneyInformation({
  cfg,
  data,
  updateData,
}) {
  const navigate = useNavigate();
  const surfaceCfg = getAttorneySurfaceConfig("retrieval");

  const [attorneyInvolved, setAttorneyInvolved] = useState(
    data.attorneyInvolved || cfg.attorneyInvolvedDefault
  );

  const core = useAttorneyInformationCore({
    surface: surfaceCfg.surface,
    cfg,
    data,
    updateData,
    onLawFirmCreated: (lawFirmId) => updateData({ ...data, lawFirm: lawFirmId }),
    onLawFirmSelected: (lawFirmId) => updateData({ ...data, lawFirm: lawFirmId }),
    onAddressCreated: (locationId) =>
      updateData({ ...data, lawFirmAddress: locationId }),
    onAttorneyCreated: (attorneyId) =>
      updateData({ ...data, attorneyOfRecord: attorneyId }),
  });

  const {
    lawFirms,
    setLawFirmLocations,
    setAttorneys,
    setNewAttorney,
    setNewLawFirm,
    setNewAddress,
    fetchLawFirms,
    fetchLocationsByLawFirm,
    fetchAttorneysByLawFirm,
    selectedLocation,
    ...coreRest
  } = core;

  useEffect(() => {
    if (!surfaceCfg.hydrateDialogStateFromData) return;
    if (data.attorneyOfRecord) {
      setNewAttorney({
        name: data.attorneyOfRecordName || "",
        phone: data.attorneyOfRecordPhone || "",
      });
    }
    if (data.lawFirm) {
      setNewLawFirm({
        name: data.lawFirmName || "",
        phone: data.lawFirmPhone || "",
      });
    }
    if (data.lawFirmAddress) {
      setNewAddress({
        address: data.lawFirmAddress || "",
        zip: data.zip || "",
        city: data.city || "",
        county: data.county || "",
        state: data.state || "",
        phone: data.phone || "",
      });
    }
    if (data.attorneyInvolved) {
      setAttorneyInvolved(data.attorneyInvolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (data.lawFirm && lawFirms.length > 0) {
      const selectedLawFirm = lawFirms.find((firm) => firm.value === data.lawFirm);
      if (selectedLawFirm) {
        fetchLocationsByLawFirm(selectedLawFirm.value);
        fetchAttorneysByLawFirm(selectedLawFirm.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.lawFirm, lawFirms]);

  useEffect(() => {
    fetchLawFirms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLawFirmChange = (event, newValue) => {
    if (newValue) {
      updateData({
        ...data,
        lawFirm: newValue.value,
        attorneyInvolved,
      });
      fetchLocationsByLawFirm(newValue.value);
      fetchAttorneysByLawFirm(newValue.value);
    } else {
      updateData({ ...data, lawFirm: "" });
      setLawFirmLocations([]);
      setAttorneys([]);
    }
  };

  return {
    navigate,
    attorneyInvolved,
    setAttorneyInvolved,
    lawFirms,
    selectedLocation,
    handleLawFirmChange,
    setNewAttorney,
    setNewLawFirm,
    setNewAddress,
    ...coreRest,
  };
}
